import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const HistoryCardinalate: React.FC = () => {
  return (
    <div className="container mx-auto px-4 py-8">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-gray-800 rounded-lg shadow-xl overflow-hidden"
      >
        <div className="overflow-hidden mb-6">
          <img 
            src="https://blogger.googleusercontent.com/img/b/R29vZ2xl/AVvXsEhQpmU6jd9_jZ0pgzLNv7wIlr3hr96ChP7Sa5nhyphenhyphenXgMSK1_EKcHbnKTNVwjo9U-UobwgbI9sFGJ-MMe1KYv4gMZJFLexFc0kexvE5ZBRsSqVhn4yT9O_o7YPyN8s1G2ppZ3FbYOYQuN6u8/s1600/d2.jpg" 
            alt="Collège des Cardinaux" 
            className="w-full h-64 object-cover object-center"
          />
        </div>
        <div className="p-6 md:p-8">
          <h1 className="text-3xl font-bold text-yellow-500 mb-6 text-center">Histoire du Cardinalat</h1>
          
          <div className="prose prose-lg prose-invert max-w-none">
            <h2 className="text-2xl font-semibold text-yellow-400 mt-8 mb-4">Origines du Cardinalat</h2>
            <p>
              Le terme "cardinal" dérive du latin <em>cardo</em> (gond, pivot), symbolisant le rôle fondamental de ces ecclésiastiques comme "pivots" sur lesquels repose la gouvernance de l'Église. Les premières mentions de cardinaux remontent au IVe siècle, mais leur fonction était alors bien différente de celle que nous connaissons aujourd'hui.
            </p>
            <p>
              À l'origine, les cardinaux étaient simplement les prêtres titulaires des principales églises de Rome, appelées "églises cardinalices". Ils assistaient l'évêque de Rome (le pape) dans l'administration du diocèse et la célébration des liturgies. Cette organisation locale s'est progressivement transformée en une institution d'envergure universelle.
            </p>
            
            <h2 className="text-2xl font-semibold text-yellow-400 mt-8 mb-4">Évolution Médiévale</h2>
            <p>
              C'est au XIe siècle, sous le pontificat de Nicolas II (1059-1061), que le rôle des cardinaux connaît une transformation décisive. Par le décret <em>In Nomine Domini</em> (1059), le pape confie aux cardinaux-évêques la responsabilité principale dans l'élection du souverain pontife, établissant ainsi les fondements du conclave moderne.
            </p>
            <p>
              Au XIIe siècle, le collège des cardinaux s'affirme comme un véritable sénat ecclésiastique. Le pape Alexandre III, par le troisième concile du Latran (1179), établit que l'élection papale requiert désormais une majorité des deux tiers des cardinaux, règle qui perdure encore aujourd'hui.
            </p>
            <p>
              Durant la période médiévale, les cardinaux acquièrent une influence considérable, non seulement dans les affaires ecclésiastiques mais aussi dans la diplomatie européenne. Beaucoup d'entre eux sont issus des grandes familles aristocratiques et servent d'intermédiaires entre le Saint-Siège et les puissances temporelles.
            </p>
            
            <h2 className="text-2xl font-semibold text-yellow-400 mt-8 mb-4">Époque Moderne</h2>
            <p>
              Le concile de Trente (1545-1563) marque un tournant dans l'histoire du cardinalat. Face aux défis de la Réforme protestante, l'Église redéfinit le profil des cardinaux, insistant davantage sur leurs qualités morales et théologiques que sur leur extraction sociale. Le pape Sixte V, par la bulle <em>Postquam verus</em> (1586), fixe à 70 le nombre maximal de cardinaux, structure qui restera en vigueur jusqu'au XXe siècle.
            </p>
            <p>
              Aux XVIIe et XVIIIe siècles, les cardinaux continuent de jouer un rôle diplomatique crucial, représentant souvent les intérêts du Saint-Siège auprès des cours européennes. La figure du "cardinal-neveu" (un parent du pape élevé au cardinalat) est emblématique de cette période, bien que cette pratique ait été progressivement abandonnée.
            </p>
            
            <h2 className="text-2xl font-semibold text-yellow-400 mt-8 mb-4">Ère Contemporaine</h2>
            <p>
              Le XXe siècle voit une internationalisation sans précédent du collège cardinalice. Le pape Pie XII commence cette transformation, mais c'est surtout Jean XXIII et Paul VI qui, dans l'esprit du concile Vatican II (1962-1965), élargissent considérablement la représentation géographique des cardinaux.
            </p>
            <p>
              Jean-Paul II poursuit cette internationalisation et, en 1996, par la constitution apostolique <em>Universi Dominici Gregis</em>, réforme les règles du conclave tout en maintenant l'essentiel de la tradition. Il limite également le droit de vote aux cardinaux de moins de 80 ans.
            </p>
            <p>
              Le pape François a encore accentué cette diversification, nommant des cardinaux issus de régions auparavant peu représentées, notamment d'Asie, d'Afrique et d'Océanie. Il a également modifié la composition du collège en privilégiant des pasteurs de "périphéries" plutôt que des titulaires de sièges traditionnellement cardinalices.
            </p>
            
            <h2 className="text-2xl font-semibold text-yellow-400 mt-8 mb-4">Le Collège Aujourd'hui</h2>
            <p>
              Aujourd'hui, le collège des cardinaux compte environ 120 membres électeurs (moins de 80 ans), auxquels s'ajoutent les cardinaux non-électeurs. Ils sont répartis en trois ordres : cardinaux-évêques, cardinaux-prêtres et cardinaux-diacres, bien que cette distinction soit devenue largement honorifique.
            </p>
            <p>
              Le collège reflète désormais la diversité de l'Église universelle, avec des représentants de tous les continents. Cette évolution témoigne de la volonté de l'Église de s'adapter aux réalités du monde contemporain tout en préservant une institution millénaire qui a su traverser les âges.
            </p>
          </div>
          
          <div className="mt-10 flex justify-between">
            <Link 
              to="/" 
              className="text-yellow-500 hover:text-yellow-400 transition-colors"
            >
              ← Retour à l'accueil
            </Link>
            <div className="flex space-x-4">
              <Link 
                to="/cardinal-roles" 
                className="text-yellow-500 hover:text-yellow-400 transition-colors"
              >
                Rôles des Cardinaux →
              </Link>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default HistoryCardinalate;
